import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { services } from "../data/services";
import { locations } from "../data/locations";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_5612de96-9817-4dde-8f5e-f254b2b066d6/artifacts/5ie5ityv_Untitled%20%283000%20x%20800%20px%29.png";

export default function Footer() {
  const featured = services.slice(0, 8);
  const popularLocations = [
    "defence-colony", "saket", "hauz-khas", "greater-kailash", "lajpat-nagar",
    "connaught-place", "dwarka", "rohini", "karol-bagh", "noida", "gurugram", "ghaziabad",
  ].map((s) => locations.find((l) => l.slug === s)).filter(Boolean);
  return (
    <footer className="bg-brand text-white relative overflow-hidden grain" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="bg-white inline-block p-4">
              <img src={LOGO_URL} alt="K & K Associates" className="h-16 w-auto" />
            </div>
            <p className="mt-8 font-serif text-lg text-white/85 leading-relaxed">
              World-class legal services at competitive rates — engineered to be lucrative for every organisation.
            </p>
            <p className="mt-6 text-xs tracking-[0.3em] uppercase text-white/60">Advocates &amp; Solicitors</p>
          </div>

          <div className="md:col-span-3">
            <h4 className="kicker text-white/80 mb-6">Practice Areas</h4>
            <ul className="space-y-3 font-serif text-base">
              {featured.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-white/85 hover:text-white link-underline"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title.split("—")[0].trim()}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-white font-medium link-underline" data-testid="footer-all-services">
                  View all {services.length} services →
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="kicker text-white/80 mb-6">Firm</h4>
            <ul className="space-y-3 font-serif text-base">
              <li><Link to="/about" className="text-white/85 hover:text-white link-underline" data-testid="footer-about">About</Link></li>
              <li><Link to="/company-profile" className="text-white/85 hover:text-white link-underline" data-testid="footer-company-profile">Company Profile</Link></li>
              <li><Link to="/locations" className="text-white/85 hover:text-white link-underline" data-testid="footer-locations">Locations We Serve</Link></li>
              <li><Link to="/insights" className="text-white/85 hover:text-white link-underline" data-testid="footer-insights">Insights</Link></li>
              <li><Link to="/contact" className="text-white/85 hover:text-white link-underline" data-testid="footer-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="kicker text-white/80 mb-6">Contact</h4>
            <ul className="space-y-4 text-white/85 font-serif text-base">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                <span data-testid="footer-address">A-197, LGF, Defence Colony,<br />New Delhi – 110024</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                <a href="tel:919643572870" className="link-underline" data-testid="footer-phone">+91 96435 72870, 05, 06</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                <a href="mailto:info@kandkassociates.in" className="link-underline" data-testid="footer-email">info@kandkassociates.in</a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                <a
                  href="https://wa.me/919643572870"
                  target="_blank" rel="noreferrer"
                  className="link-underline"
                  data-testid="footer-whatsapp"
                >
                  +91 96435 72870
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15" data-testid="footer-locations-strip">
          <h4 className="kicker text-white/80 mb-5">Lawyers Across Delhi NCR</h4>
          <div className="flex flex-wrap gap-x-5 gap-y-2 font-serif text-sm">
            {popularLocations.map((l) => (
              <Link
                key={l.slug}
                to={`/locations/${l.slug}`}
                className="text-white/75 hover:text-white link-underline"
                data-testid={`footer-location-${l.slug}`}
              >
                Lawyers in {l.name}
              </Link>
            ))}
            <Link to="/locations" className="text-white font-medium link-underline" data-testid="footer-location-all">
              View all 100 locations →
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/60 tracking-wider">
          <p>© {new Date().getFullYear()} K &amp; K Associates · All rights reserved.</p>
          <p className="font-serif italic text-white/70">
            “Advising clients on how <span className="text-white">not to litigate</span>.”
          </p>
          <p className="text-white/60">
            Developed by{" "}
            <a
              href="https://www.brandurbusiness.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white link-underline"
              data-testid="footer-developer-credit"
            >
              Brand Ur Business
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
